import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'react-bootstrap';

import { DropdownMenu, DropdownToggle } from '@/components/dropdown';
import { createUseThemedStyles } from '@/jss/theme';
import { MhicAssignOrderModal } from './mhic-assign-order-modal';
import useFlags from '@/hooks/use-flags';
import { PatientOrderDispositionId, PatientOrderModel, PatientOrderResourcingStatusId } from '@/lib/models';
import { integratedCareService } from '@/lib/services';

import SvgIcon from '@/components/svg-icon';
import { useNavigate, useRevalidator } from 'react-router-dom';
import { MhicScheduleAssessmentModal } from './mhic-schedule-assessment-modal';
import { MhicVoicemailTaskModal } from './mhic-voicemail-task-modal';
import { MhicCloseEpisodeModal } from './mhic-close-episode-modal';
import useHandleError from '@/hooks/use-handle-error';
import { MhicScheduleCallModal } from './mhic-schedule-call-modal';

const useStyles = createUseThemedStyles(() => ({
	shelfMoreButton: {
		top: 20,
		right: 64,
	},
}));

interface MhicPatientOrderShelfActionsProps {
	patientOrder: PatientOrderModel;
}

const NO_ACTION_DISPOSITIONS = [PatientOrderDispositionId.CLOSED, PatientOrderDispositionId.ARCHIVED];

export const MhicPatientOrderShelfActions = ({ patientOrder }: MhicPatientOrderShelfActionsProps) => {
	const handleError = useHandleError();
	const classes = useStyles();
	const { addFlag } = useFlags();
	const navigate = useNavigate();
	const revalidator = useRevalidator();

	const [showAssignOrderModal, setShowAssignOrderModal] = useState(false);
	const [showScheduleAssessmentModal, setShowScheduleAssessmentModal] = useState(false);
	const [showAddVoicemailTaskModal, setShowAddVoicemailTaskModal] = useState(false);
	const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
	const [showCloseEpisodeModal, setShowCloseEpisodeModal] = useState(false);

	const handleAssignOrdersSave = useCallback(
		async (_patientOrderCount: number, panelAccountDisplayName: string) => {
			revalidator.revalidate();
			setShowAssignOrderModal(false);
			addFlag({
				variant: 'success',
				title: 'Patient assigned',
				description: `Patient assigned to ${panelAccountDisplayName}`,
				actions: [],
			});
		},
		[addFlag, revalidator]
	);

	if (NO_ACTION_DISPOSITIONS.includes(patientOrder.patientOrderDispositionId)) {
		return null;
	}

	const assessmentInProgress = !!patientOrder.screeningSession && !patientOrder.screeningSession.completed;
	const assessmentCompleted = !!patientOrder.screeningSession && patientOrder.screeningSession.completed;
	const canAddResourceRequest =
		patientOrder.patientOrderResourcingStatusId === PatientOrderResourcingStatusId.NONE_NEEDED;

	return (
		<>
			<MhicAssignOrderModal
				patientOrderIds={[patientOrder.patientOrderId]}
				show={showAssignOrderModal}
				onHide={() => {
					setShowAssignOrderModal(false);
				}}
				onSave={handleAssignOrdersSave}
			/>

			<MhicScheduleAssessmentModal
				patientOrder={patientOrder}
				show={showScheduleAssessmentModal}
				onHide={() => {
					setShowScheduleAssessmentModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowScheduleAssessmentModal(false);
				}}
			/>

			<MhicVoicemailTaskModal
				patientOrder={patientOrder}
				show={showAddVoicemailTaskModal}
				onHide={() => {
					setShowAddVoicemailTaskModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowAddVoicemailTaskModal(false);
				}}
			/>

			<MhicScheduleCallModal
				patientOrder={patientOrder}
				show={showScheduleCallModal}
				onHide={() => {
					setShowScheduleCallModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowScheduleCallModal(false);
				}}
			/>

			<MhicCloseEpisodeModal
				show={showCloseEpisodeModal}
				onHide={() => {
					setShowCloseEpisodeModal(false);
				}}
				onSave={async (patientOrderClosureReasonId) => {
					try {
						await integratedCareService
							.closePatientOrder(patientOrder.patientOrderId, { patientOrderClosureReasonId })
							.fetch();

						revalidator.revalidate();
						setShowCloseEpisodeModal(false);
						addFlag({
							variant: 'success',
							title: 'Episode Closed',
							actions: [],
						});
					} catch (error) {
						handleError(error);
					}
				}}
			/>

			<Dropdown className={classNames(classes.shelfMoreButton, 'position-absolute')}>
				<Dropdown.Toggle as={DropdownToggle} id={`mhic-shelf__dropdown-menu`} className="p-2 border-0">
					<SvgIcon kit="far" icon="ellipsis" size={16} className="d-flex" />
				</Dropdown.Toggle>
				<Dropdown.Menu as={DropdownMenu} align="end" popperConfig={{ strategy: 'fixed' }} renderOnMount>
					<Dropdown.Item
						className="d-flex align-items-center"
						onClick={() => {
							setShowAssignOrderModal(true);
						}}
					>
						<SvgIcon kit="far" icon="user-plus" size={20} className="me-2 text-gray" /> Assign MHIC
					</Dropdown.Item>
					<Dropdown.Item
						className="d-flex align-items-center"
						onClick={() => {
							setShowAddVoicemailTaskModal(true);
						}}
					>
						<SvgIcon kit="far" icon="voicemail" size={20} className="me-2 text-gray" /> Add Voicemail Task
					</Dropdown.Item>
					<Dropdown.Item
						className="d-flex align-items-center"
						onClick={() => {
							setShowScheduleCallModal(true);
						}}
					>
						<SvgIcon kit="far" icon="calendar-clock" size={20} className="me-2 text-gray" /> Schedule Call
					</Dropdown.Item>

					{!assessmentInProgress && !assessmentCompleted && (
						<>
							<Dropdown.Divider />
							<Dropdown.Item
								className="d-flex align-items-center"
								onClick={() => {
									navigate(`/ic/mhic/order-assessment/${patientOrder.patientOrderId}`);
								}}
							>
								<SvgIcon kit="far" icon="clipboard-list-check" size={20} className="me-2 text-gray" />{' '}
								Start Assessment
							</Dropdown.Item>
							<Dropdown.Item
								className="d-flex align-items-center"
								onClick={() => {
									setShowScheduleAssessmentModal(true);
								}}
							>
								<SvgIcon kit="far" icon="calendar-clock" size={20} className="me-2 text-gray" />{' '}
								Schedule Assessment
							</Dropdown.Item>
						</>
					)}
					{assessmentCompleted && canAddResourceRequest && (
						<>
							<Dropdown.Divider />
							<Dropdown.Item
								className="d-flex align-items-center"
								onClick={async () => {
									await integratedCareService
										.updateResourcingStatus(patientOrder.patientOrderId, {
											patientOrderResourcingStatusId:
												PatientOrderResourcingStatusId.NEEDS_RESOURCES,
										})
										.fetch();

									revalidator.revalidate();
								}}
							>
								<SvgIcon kit="far" icon="note" size={20} className="me-2 text-gray" /> Add Resource
								Request
							</Dropdown.Item>
						</>
					)}
					<Dropdown.Divider />
					<Dropdown.Item
						className="d-flex align-items-center"
						onClick={() => {
							setShowCloseEpisodeModal(true);
						}}
					>
						<SvgIcon kit="far" icon="file-circle-xmark" size={20} className="me-2 text-gray" /> Close
						Episode
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		</>
	);
};
